import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { SelectOption } from '@kep-ui-kit/ui-components';
import {
  StyledFormGroup,
  StyledInput,
  StyledSelectMenu,
  StyledSubTitle,
} from '@selfserve/react-ui';
import { useCountryList, FormValues } from '@selfserve/react-hooks';
import { IIndexSignature } from '@selfserve/react-utils';

interface Props {
  formik: FormikProps<FormValues>;
  handleDropdownChange: (name: string) => (value: string) => void;
  handleZipCodeBlur?: () => void;
  reverse?: boolean;
}

export const CheckoutBillingForm = ({
  formik: { handleChange, handleBlur, values, errors, touched },
  handleDropdownChange,
  handleZipCodeBlur,
  reverse,
}: Props) => {
  const { t } = useTranslation();
  const { countries, states, EUVATCountryCodes } = useCountryList();
  const currentStates = useMemo(
    () => (states as IIndexSignature<SelectOption[]>)[values.country as string],
    [states, values]
  );

  return (
    <div>
      <StyledSubTitle>{t('common.billing-information')}</StyledSubTitle>
      <StyledFormGroup>
        <StyledInput
          id="address"
          name="address"
          inputLabel={t('common.address')}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.address}
          error={touched.address ? !!errors.address : false}
          inputProps={{ 'aria-label': t('aria.address') }}
          autoFocus={!reverse}
          fullWidth
        />
        {touched.address && errors.address && (
          <p className="p-error">{errors.address}</p>
        )}
      </StyledFormGroup>
      <StyledFormGroup row>
        <StyledFormGroup withoutMargin>
          <StyledInput
            id="city"
            name="city"
            inputLabel={t('common.city')}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.city}
            error={touched.city ? !!errors.city : false}
            inputProps={{ 'aria-label': t('aria.city') }}
            fullWidth
          />
          {touched.city && errors.city && (
            <p className="p-error">{errors.city}</p>
          )}
        </StyledFormGroup>
        <StyledFormGroup withoutMargin>
          <StyledInput
            id="zip"
            name="zipCode"
            inputLabel={t('common.zip-code')}
            onChange={handleChange}
            onBlur={(event) => {
              handleBlur(event);
              if (handleZipCodeBlur) handleZipCodeBlur();
            }}
            value={values.zipCode}
            error={touched.zipCode ? !!errors.zipCode : false}
            inputProps={{ 'aria-label': t('aria.zip-code') }}
            fullWidth
          />
          {touched.zipCode && errors.zipCode && (
            <p className="p-error">{errors.zipCode}</p>
          )}
        </StyledFormGroup>
      </StyledFormGroup>
      <StyledFormGroup row>
        <StyledFormGroup withoutMargin>
          <StyledSelectMenu
            fullWidth
            className="p-dropdown"
            inputLabel={t('common.country')}
            placeholder={t('common.select')}
            options={countries}
            name="country"
            value={values.country}
            defaultValue={values.country}
            onChange={handleDropdownChange('country')}
            onSelect={handleBlur}
          />
          {touched.country && errors.country && (
            <p className="p-error">{errors.country}</p>
          )}
        </StyledFormGroup>
        <StyledFormGroup withoutMargin>
          {currentStates?.length && (
            <>
              <StyledSelectMenu
                fullWidth
                className="p-dropdown"
                inputLabel={t('common.state')}
                placeholder={t('common.select')}
                options={currentStates}
                name="state"
                value={values.state}
                onChange={handleDropdownChange('state')}
                onSelect={handleBlur}
              />
              {touched.state && errors.state && (
                <p className="p-error">{errors.state}</p>
              )}
            </>
          )}
          {EUVATCountryCodes.includes(values.country || '') && (
            <>
              <StyledInput
                id="vatNumber"
                name="vatNumber"
                inputLabel={t('common.vat-number')}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.vatNumber}
                error={touched.vatNumber ? !!errors.vatNumber : false}
                inputProps={{ 'aria-label': t('aria.vat-number') }}
                fullWidth
              />
              {touched.vatNumber && errors.vatNumber && (
                <p className="p-error">{errors.vatNumber}</p>
              )}
            </>
          )}
        </StyledFormGroup>
      </StyledFormGroup>
    </div>
  );
};
